import { format } from 'date-fns';

import {
  category,
  categoryRange,
  range,
} from '../../../packages/configs/layer-builder';
import { censusTimelineLayers } from '../../../packages/configs/census-tools';
import {
  overlayLayers,
  // codeEnforcement,
} from '../../../packages/configs/layer-sets';

// codeEnforcement.layers = codeEnforcement.layers.filter(({ key }) => key !== 'open_violation_count');

const battleCreekOverlayLayers = () => ({
  ...overlayLayers,
  layers: [
    category({
      label: 'CDBG Target Areas',
      key: 'name+cdbg',
      source: 'gis_neighborhoods',
      filterString: 'type=CDBG Target Area',
      style: 'line',
    }),
    {
      label: 'Neighborhood Planning Councils',
      key: 'name',
      source: 'gis_neighborhoods',
      default: true,
      filterString: 'type=Neighborhood Planning Council',
      style: {
        type: 'line',
        paint: {
          'line-color': {
            type: 'categorical',
            property: 'name',
            default: 'transparent',
            stops: [
              ['Central', '#0A00FE'],
              ['Downtown Business District', '#0CFDD1'],
              ['Minges Brook Riverside', '#FC0700'],
              ['North Central', '#FBD101'],
              ['Northeast', '#FE6202'],
              ['Post Franklin', '#05A0FC'],
              ['Rural Southwest', '#E906FE'],
              ['Urbandale', '#E2E601'],
              ['W.K. Kellogg Airport / Fort Custer Industrial Park', '#40E903'],
              ['Westlake Prarieview', '#0BFF6C'],
            ],
          },
          'line-width': 3,
          'line-dasharray': [2, 2],
        },
      },
    },
    ...overlayLayers.layers,
  ],
});

export const layers = [
  {
    header: 'Parcel Layers',
    id: 'Parcel-layers',
    layers: [
      category({
        label: 'General Zoning',
        key: 'general_zoning',
        forceDots: true,
        download: {
          keys: ['address', 'parcel_id', 'general_zoning', 'zoning'],
        },
      }),
      category({
        label: 'Zoning',
        key: 'zoning',
        forceDots: true,
        download: {
          keys: ['address', 'parcel_id', 'zoning', 'general_zoning'],
        },
      }),

      categoryRange({
        label: 'Tax Status',
        key: 'tax_status',
        forceDots: true,
        source: 'parcel_history',
        download: {
          keys: ['address', 'update_date', 'parcel_id', 'tax_status'],
        },
        timeline: {
          dateColumn: 'update_date',
          source: 'parcel_history',
          key: 'tax_status',
          displayFirstQuarter: true,
          start: 'Jan 1 2010',
          end: format(new Date(), 'LLL d y'),
        },
        unit: 'text_map',
        map: {
          'Tax Current': 'Tax Current',
          'Tax Exempt': 'Tax Exempt',
          'Tax Delinquent': 'Tax Delinquent',
          'Tax Foreclosable': 'Tax Forfeiture',
        },
        values: [
          'Tax Current',
          'Tax Exempt',
          'Tax Delinquent',
          'Tax Foreclosable',
        ],
      }),
      categoryRange({
        label: 'Occupancy',
        key: 'is_vacant',
        source: 'parcel_history',
        forceDots: true,
        download: {
          keys: ['address', 'update_date', 'parcel_id', 'is_vacant'],
        },
        unit: 'text_map',
        timeline: {
          dateColumn: 'update_date',
          source: 'parcel_history',
          key: 'is_vacant',
          displayFirstQuarter: true,
          start: 'Jan 1 2010',
          end: format(new Date(), 'LLL d y'),
        },
        map: {
          0: 'Occupied',
          1: 'Unoccupied',
        },
        values: [0, 1],
      }),

      categoryRange({
        label: 'Owner Occupancy',
        key: 'owner_occupancy',
        source: 'parcel_history',
        forceDots: true,
        download: {
          keys: ['address', 'update_date', 'parcel_id', 'owner_occupancy'],
        },
        colorScheme: 'tricolor-4',
        unit: 'text_map',
        timeline: {
          dateColumn: 'update_date',
          source: 'parcel_history',
          key: 'owner_occupancy',
          displayFirstQuarter: true,
          start: 'Jan 1 2012',
          end: format(new Date(), 'LLL d y'),
        },
        map: {
          0: 'Not Owner-Occupied',
          1: 'Owner-Occupied',
        },
        values: [0, 1],
      }),
      range({
        label: 'Residential Sales',
        key: 'amount',
        source: 'transactions',
        forceDots: true,
        unit: 'dollars',
        style: 'circle',
        colorScheme: 'positive-1',
        filterString: 'transaction_type=Valid%20Sale&prop_type=Residential',
        timeline: {
          dateColumn: 'sale_date',
          source: 'transactions',
          key: 'amount',
          start: 'Jan 1 2011',
          end: format(new Date(), 'LLL d y'),
        },
        download: {
          keys: [
            'address',
            'parcel_id',
            'amount',
            'sale_date',
            'grantor',
            'grantee',
          ],
        },
      }),
      range({
        label: 'Commercial Sales',
        key: 'amount+comm',
        source: 'transactions',
        forceDots: true,
        unit: 'dollars',
        style: 'circle',
        colorScheme: 'positive-2',
        filterString: 'transaction_type=Valid%20Sale&prop_type=Commercial',
        timeline: {
          dateColumn: 'sale_date',
          source: 'transactions',
          key: 'amount+comm',
          start: 'Jan 1 2011',
          end: format(new Date(), 'LLL d y'),
        },
        download: {
          keys: [
            'address',
            'parcel_id',
            'amount',
            'sale_date',
            'grantor',
            'grantee',
          ],
        },
      }),
      range({
        label: 'Res. Sales in the last year',
        key: 'amount+lastyear',
        source: 'transactions',
        forceDots: true,
        unit: 'dollars',
        style: 'circle',
        colorScheme: 'positive-1',
        filterString:
          'sale_date=inthelast%3A1%20year&transaction_type=Valid%20Sale&prop_type=Residential',
        download: {
          keys: [
            'address',
            'parcel_id',
            'amount',
            'sale_date',
            'grantor',
            'grantee',
          ],
        },
      }),

      range({
        label: 'Com. Sales in the last year',
        key: 'amount+lastyearcom',
        source: 'transactions',
        forceDots: true,
        unit: 'dollars',
        style: 'circle',
        colorScheme: 'positive-2',
        filterString:
          'sale_date=inthelast%3A1%20year&transaction_type=Valid%20Sale&prop_type=Commercial',
        download: {
          keys: [
            'address',
            'parcel_id',
            'amount',
            'sale_date',
            'grantor',
            'grantee',
          ],
        },
      }),
      categoryRange({
        label: 'Mortgage Foreclosures',
        key: 'transaction_type+mortgage',
        source: 'transactions',
        forceDots: true,
        style: 'circle',
        filterString: 'transaction_type=Mortgage%20Foreclosure',
        timeline: {
          dateColumn: 'sale_date',
          source: 'transactions',
          key: 'transaction_type+mortgage',
          start: 'Jan 1 2011',
          end: format(new Date(), 'LLL d y'),
        },
        values: ['Mortgage Foreclosure'],
        download: {
          keys: [
            'address',
            'parcel_id',
            'transaction_type',
            'sale_date',
            'grantor',
            'grantee',
          ],
        },
      }),

      categoryRange({
        label: 'Tax Foreclosures',
        key: 'transaction_type+tax',
        colorScheme: 'negative-1',
        source: 'transactions',
        forceDots: true,
        filterString: 'transaction_type=Tax%20Foreclosure',
        timeline: {
          dateColumn: 'sale_date',
          source: 'transactions',
          key: 'transaction_type+tax',
          start: 'Jan 1 2011',
          end: format(new Date(), 'LLL d y'),
        },
        style: 'circle',
        values: ['Tax Foreclosure'],
        download: {
          keys: [
            'address',
            'parcel_id',
            'transaction_type',
            'sale_date',
            'grantor',
            'grantee',
          ],
        },
      }),

      category({
        label: 'Current Use',
        key: 'land_use',
        forceDots: true,
        download: {
          keys: ['address', 'parcel_id', 'land_use'],
        },
      }),

      categoryRange({
        label: 'Has Structure',
        key: 'structure',
        forceDots: true,
        download: {
          keys: ['address', 'parcel_id', 'structure'],
        },
        values: ['Yes', 'No'],
      }),

      range({
        label: 'Year Built',
        key: 'year_built',
        forceDots: true,
        download: {
          keys: ['address', 'parcel_id', 'year_built'],
        },
        colorScheme: 'tricolor-1',
      }),

      category({
        label: 'Zip Codes',
        key: 'zip',
        forceDots: true,
        download: {
          keys: ['address', 'parcel_id', 'zip'],
        },
      }),
    ],
  },

  {
    header: 'Impact Layers',
    layers: [
      range({
        label: 'Rehabilitation Impact',
        key: 'rehab_impact',
        source: 'rehab_impact',
        orLess: 0,
        forceDots: true,
        unit: 'dollars',
        colorScheme: 'positive-3',
        download: {
          keys: [
            'address',
            'parcel_id',
            'date',
            'rehab_impact_txt',
            'avg_hom_val',
            'impact_per',
          ],
        },
      }),
      range({
        label: 'Predicted Rehabilitation Impact',
        key: 'rehab_impact_500',
        source: 'predicted_impact',
        orLess: 0,
        forceDots: true,
        unit: 'dollars',
        colorScheme: 'positive-3',
        download: {
          keys: ['address', 'parcel_id', 'demo_impact_500', 'rehab_impact_500'],
        },
      }),
      range({
        label: 'Demolition Impact',
        key: 'demo_impact',
        source: 'demo_impact',
        orLess: 0,
        forceDots: true,
        unit: 'dollars',
        colorScheme: 'positive-2',
        download: {
          keys: [
            'address',
            'parcel_id',
            'date',
            'demo_impact_txt',
            'avg_hom_val',
            'impact_per',
          ],
        },
      }),
      range({
        label: 'Predicted Demolition Impact',
        key: 'demo_impact_500',
        source: 'predicted_impact',
        orLess: 0,
        forceDots: true,
        unit: 'dollars',
        colorScheme: 'positive-2',
        download: {
          keys: ['address', 'parcel_id', 'demo_impact_500', 'rehab_impact_500'],
        },
      }),
    ],
  },
  // codeEnforcement,

  {
    header: 'Heatmap Layers',
    layers: [
      range({
        label: 'Residential Price Per Sq. Foot',
        key: 'res_sp_sqft+1000',
        source: 'hex1000data',
        unit: 'dollars',
        colorScheme: 'positive-1',
        opacity: 0.6,
      }),
      range({
        label: 'Building Density (sq. ft. per hexagon)',
        key: 'all_sqft+1000',
        source: 'hex1000data',
        colorScheme: 'positive-1',
        opacity: 0.6,
      }),
      range({
        label: 'Density (parcels per hexagon)',
        key: 'count+1000',
        source: 'hex1000data',
        colorScheme: 'positive-1',
        opacity: 0.6,
      }),
      range({
        label: 'Percent Vacant Lots',
        key: 'vacant_lot+1000',
        source: 'hex1000data',
        unit: 'decimal_percent',
        colorScheme: 'tricolor-2',
        opacity: 0.6,
      }),
      range({
        label: 'Percent Tax Delinquent',
        key: 'tax_delinquent+1000',
        source: 'hex1000data',
        unit: 'decimal_percent',
        colorScheme: 'tricolor-2',
        opacity: 0.6,
      }),
      range({
        label: 'Percent Tax Foreclosure',
        key: 'tax_foreclosure+1000',
        source: 'hex1000data',
        unit: 'decimal_percent',
        colorScheme: 'tricolor-3',
        opacity: 0.6,
        values: [0, 0.1],
      }),
      range({
        label: 'Percent Mortgage Foreclosure',
        key: 'mortgage_foreclosure+1000',
        source: 'hex1000data',
        unit: 'decimal_percent',
        colorScheme: 'tricolor-3',
        opacity: 0.6,
        values: [0, 0.1],
      }),
      range({
        label: 'Percent Owner Occupied',
        key: 'owner_occ+1000',
        source: 'hex1000data',
        unit: 'decimal_percent',
        colorScheme: 'tricolor-1',
        opacity: 0.6,
      }),
      range({
        label: 'Percent Occupied',
        key: 'occupied+1000',
        source: 'hex1000data',
        unit: 'decimal_percent',
        colorScheme: 'tricolor-1',
        opacity: 0.6,
      }),
    ],
  },
  {
    header: 'Heatmap Change Layers',
    layers: [
      range({
        label: 'Change in Residential Price Per Sq. Foot - 1 year',
        key: 'res_sp_sqft_1y+1000',
        source: 'hex1000data',
        unit: 'decimal_percent',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-0.9, -0.6, -0.3, 0, 0.3, 0.6, 0.9],
      }),
      range({
        label: 'Change in Residential Price Per Sq. Foot - 5 year',
        key: 'res_sp_sqft_5y+1000',
        source: 'hex1000data',
        unit: 'decimal_percent',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-0.9, -0.6, -0.3, 0, 0.3, 0.6, 0.9],
      }),
      range({
        label: 'Change in Tax Delinquency - 1 year',
        key: 'tax_delinquent_1y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Tax Delinquency - 5 year',
        key: 'tax_delinquent_5y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Tax Foreclosure - 1 year',
        key: 'tax_foreclosure_1y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Tax Foreclosure - 5 year',
        key: 'tax_foreclosure_5y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Mortgage Foreclosure - 1 year',
        key: 'mortgage_foreclosure_1y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Mortgage Foreclosure - 5 year',
        key: 'mortgage_foreclosure_5y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Occupancy - 1 year',
        key: 'occupied_1y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Occupancy - 5 year',
        key: 'occupied_5y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Owner Occupancy - 1 year',
        key: 'owner_occ_1y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Owner Occupancy - 5 year',
        key: 'owner_occ_5y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Vacant Lot - 1 year',
        key: 'vacant_lot_1y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
      range({
        label: 'Change in Vacant Lot - 5 year',
        key: 'vacant_lot_5y+1000',
        source: 'hex1000data',
        colorScheme: 'tricolor-4',
        opacity: 0.6,
        values: [-6, -4, -2, 0, 2, 4, 6],
      }),
    ],
  },
  {
    header: 'Detectors',
    id: 'parcel-change-layers',
    layers: [
      categoryRange({
        label: 'Re-Occupancy Detector',
        key: 'reoccupancy_detector',
        forceDots: true,
        download: {
          keys: ['address', 'parcel_id', 'reoccupancy_detector'],
        },
        colorScheme: 'positive-1-inverse',
        values: ['Last 3 months', 'Last 6 months', 'Last year'],
      }),
      categoryRange({
        label: 'Vacancy Detector',
        key: 'vacancy_detector',
        forceDots: true,
        download: {
          keys: ['address', 'parcel_id', 'vacancy_detector'],
        },
        colorScheme: 'negative-4-inverse',
        values: [
          'Last 3 months',
          'Last 6 months',
          'Last year',
          'More than 1 year',
        ],
      }),
    ],
  },
  censusTimelineLayers(),
  battleCreekOverlayLayers(),
];
